import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getISOWeek,
  getISOWeekYear,
  startOfISOWeek,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  addMonths,
  subMonths,
  format,
  startOfMonth,
  endOfMonth,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { Card } from '../../components/Shared/Card';
import { Loading } from '../../components/Shared/Loading';
import { EmptyState } from '../../components/Shared/EmptyState';
import { EmployeeSelector } from './components/EmployeeSelector';
import { WeekHeader } from './components/WeekHeader';
import { EmployeeRow } from './components/EmployeeRow';
import { DayView } from './components/DayView';
import { MonthView } from './components/MonthView';
import { TaskEditDialog } from './components/TaskEditDialog';
import { AbsenceDialog } from './components/AbsenceDialog';
import { WorkDayDialog, type PlannedVehicleChoice } from './components/WorkDayDialog';
import { AddTaskDialog } from './components/AddTaskDialog';
import type { AddTaskCoworker } from './components/AddTaskDialog';
import { MoveTaskDialog } from './components/MoveTaskDialog';
import { GenerateMonthDialog } from './components/GenerateMonthDialog';
import { DueTasksSidepanel } from './components/DueTasksSidepanel';
import { CopyWeekDialog } from './components/CopyWeekDialog';
import { AdHocPlanEntrySlideOver } from './AdHocPlanEntrySlideOver';
import { AssignWorkScheduleModelDialog } from '../../components/Shared/AssignWorkScheduleModelDialog';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog';
import { showApiError } from '../../errors';
import { type PlanningWeekData, type DraggedTask, type TaskBlock, type DayPlanning } from '../../types/planning';
import { usePlanningStore } from '../../store/usePlanningStore';
import { usePlanningWeeksStore } from '../../store/usePlanningWeeksStore';
import { useDueTasksStore } from '../../store/useDueTasksStore';
import { useAbsenceStore } from '../../store/useAbsenceStore';
import { useEmployeeStore } from '../../store/useEmployeeStore';
import { useToastStore } from '../../store/useToastStore';
import { useAuthStore } from '../../store/useAuthStore';
import { transformApiEntriesToWeekData, mergePlanEntriesOverlay, mergeAbsencesOverlay } from '../../utils/planningTransformer';
import { Calendar, CalendarPlus, Copy, ChevronLeft, ChevronRight, CalendarDays, CalendarRange, AlertTriangle, FileDown, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useReportDownload } from '../../hooks/useReportDownload';
import type { ReportType } from '../../utils/downloadReport';
import { addIsoDays, isoDate } from '../../utils/dateUtils';
import { Link } from 'react-router-dom';
import { isBackOfficeRole } from '../../utils/roles';
import { useVehicleStore, type VehicleDto } from '../../store/useVehicleStore';

type ViewMode = 'week' | 'day' | 'month';


export default function Planning() {
  const { entries, loading, fetchWeekEntries, createEntry, updateEntry, setPlannedVehicle, deleteEntry, addTaskToEntry, assignTaskToEmployees, removeTaskFromEntry, moveTaskToEntry, updateTask, bulkUpdateTasks, generateMonthEntries } = usePlanningStore();
  const { planEntries, fetchPlanEntries } = usePlanningWeeksStore();
  const { dueTasks, loading: dueTasksLoading, fetchDueTasks, markPlanned: markDueTaskPlanned } = useDueTasksStore();
  const { absences, fetchAbsences } = useAbsenceStore();
  const {
    employees: allEmployees,
    loading: employeesLoading,
    error: employeesError,
    fetchEmployees,
  } = useEmployeeStore();
  const { addToast } = useToastStore();
  const { isAdmin, employee: authEmployee } = useAuthStore();
  // Active vehicles for planning the vehicle of a work day (FACF-118).
  const fetchActiveVehicles = useVehicleStore((s) => s.fetchActiveVehicles);
  const [activeVehicles, setActiveVehicles] = useState<VehicleDto[]>([]);
  useEffect(() => {
    if (!isAdmin) return;
    fetchActiveVehicles()
      .then(setActiveVehicles)
      .catch(() => setActiveVehicles([]));
  }, [isAdmin, fetchActiveVehicles]);
  const { t: tReports } = useTranslation('reports');
  const { t } = useTranslation('planning');
  const { t: tWorkScheduleModels } = useTranslation('workScheduleModels');
  const { downloading: reportDownloading, download: downloadReportFn } = useReportDownload();

  const [weekData, setWeekData] = useState<PlanningWeekData | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [draggedTask, setDraggedTask] = useState<DraggedTask | null>(null);

  // View mode
  const [viewMode, setViewMode] = useState<ViewMode>('week');

  // ISO week state (used for week view)
  const [currentDate, setCurrentDate] = useState(() => startOfISOWeek(new Date()));
  const currentYear = getISOWeekYear(currentDate);
  const currentWeek = getISOWeek(currentDate);
  const currentWeekStart = format(currentDate, 'yyyy-MM-dd');

  // Day view state
  const [dayViewDate, setDayViewDate] = useState(() => new Date());

  // Month view state
  const [monthViewDate, setMonthViewDate] = useState(() => new Date());

  // Dialog states
  const [taskEditDialog, setTaskEditDialog] = useState<{
    isOpen: boolean;
    task: TaskBlock | null;
    employeeId: string;
    employeeName: string;
    date: string;
    currentDay: DayPlanning | null;
  }>({
    isOpen: false,
    task: null,
    employeeId: '',
    employeeName: '',
    date: '',
    currentDay: null,
  });
  const [absenceDialog, setAbsenceDialog] = useState<{ isOpen: boolean; employeeId: string; employeeName: string; date: string }>({
    isOpen: false,
    employeeId: '',
    employeeName: '',
    date: '',
  });
  const [workDayDialog, setWorkDayDialog] = useState<{
    isOpen: boolean;
    employeeId: string;
    employeeName: string;
    date: string;
    isEditMode?: boolean;
    planningEntryId?: string;
    initialStartTime?: string;
    initialEndTime?: string;
    initialTargetHours?: number;
    initialVehicle?: PlannedVehicleChoice;
  }>({
    isOpen: false,
    employeeId: '',
    employeeName: '',
    date: '',
  });
  const [addTaskDialog, setAddTaskDialog] = useState<{
    isOpen: boolean;
    employeeId: string;
    employeeName: string;
    date: string;
    planningEntryId?: string;
    preSelectedTaskId?: string;
    preStartTime?: string;
    preEndTime?: string;
  }>({
    isOpen: false,
    employeeId: '',
    employeeName: '',
    date: '',
  });
  const [moveTaskDialog, setMoveTaskDialog] = useState<{
    isOpen: boolean;
    task: TaskBlock | null;
    sourceDay: DayPlanning | null;
    targetDay: DayPlanning | null;
    targetEmployeeId: string;
    targetEmployeeName: string;
    targetDate: string;
  }>({
    isOpen: false,
    task: null,
    sourceDay: null,
    targetDay: null,
    targetEmployeeId: '',
    targetEmployeeName: '',
    targetDate: '',
  });
  const [generateMonthDialog, setGenerateMonthDialog] = useState(false);
  const [copyWeekDialog, setCopyWeekDialog] = useState(false);
  const [adHocSlideOverOpen, setAdHocSlideOverOpen] = useState(false);
  const [assignModelDialog, setAssignModelDialog] = useState<{
    isOpen: boolean;
    employeeId: string;
    employeeName: string;
  }>({ isOpen: false, employeeId: '', employeeName: '' });
  const [removeDayDialog, setRemoveDayDialog] = useState<{
    isOpen: boolean;
    employeeId: string;
    date: string;
  }>({ isOpen: false, employeeId: '', date: '' });

  // --- Data Loading ---
  const getWeekEnd = (weekStart: string): string => addIsoDays(weekStart, 6);

  const loadAllData = useCallback(async () => {
    try {
      const weekEnd = getWeekEnd(currentWeekStart);
      // Parallel: PlanningEntries + PlanEntries (overlay) + DueTasks + Absences + Employees
      await Promise.all([
        fetchWeekEntries(currentWeekStart, weekEnd),
        fetchPlanEntries(currentYear, currentWeek).catch(() => {
          // PlanEntries overlay ist optional - bei Fehler still ignorieren
        }),
        fetchDueTasks(currentYear, currentWeek).catch(() => {
          // DueTasks optional
        }),
        fetchAbsences({ startDate: currentWeekStart, endDate: weekEnd }).catch(() => {
          // Absences optional
        }),
        fetchEmployees({ pageSize: 200 }).catch(() => {
          // Employees optional - falls Fehler, nur Entries-basierte Liste
        }),
      ]);
    } catch {
      addToast('Fehler beim Laden der Wochendaten', 'error');
    }
  }, [currentWeekStart, currentYear, currentWeek, fetchWeekEntries, fetchPlanEntries, fetchDueTasks, fetchAbsences, fetchEmployees, addToast]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Transform entries + merge PlanEntries overlay + merge Absences overlay
  useEffect(() => {
    const baseData = transformApiEntriesToWeekData(entries, currentWeekStart, currentYear, currentWeek, allEmployees);
    const withPlanEntries = mergePlanEntriesOverlay(baseData, planEntries);
    const withAbsences = mergeAbsencesOverlay(withPlanEntries, absences);
    setWeekData(withAbsences);
  }, [entries, planEntries, absences, allEmployees, currentWeekStart, currentYear, currentWeek]);

  // Due tasks are always fetched per ISO week — in day view that is the week of the
  // displayed day, everywhere else the week the week view is on.
  const dueTasksContext = useMemo(() => {
    const ref = viewMode === 'day' ? dayViewDate : currentDate;
    return { year: getISOWeekYear(ref), week: getISOWeek(ref) };
  }, [viewMode, dayViewDate, currentDate]);

  // Day view: load entries + due tasks for selected day
  const loadDayData = useCallback(async () => {
    if (viewMode !== 'day') return;
    const dateStr = format(dayViewDate, 'yyyy-MM-dd');
    try {
      await Promise.all([
        fetchWeekEntries(dateStr, dateStr),
        fetchDueTasks(dueTasksContext.year, dueTasksContext.week).catch(() => {
          // DueTasks optional
        }),
      ]);
    } catch {
      addToast('Fehler beim Laden der Tagesdaten', 'error');
    }
  }, [viewMode, dayViewDate, fetchWeekEntries, fetchDueTasks, dueTasksContext, addToast]);

  useEffect(() => {
    if (viewMode === 'day') {
      loadDayData();
    }
  }, [viewMode, loadDayData]);

  // Month view: load entries for entire month
  const loadMonthData = useCallback(async () => {
    if (viewMode !== 'month') return;
    const monthStart = format(startOfMonth(monthViewDate), 'yyyy-MM-dd');
    const monthEnd = format(endOfMonth(monthViewDate), 'yyyy-MM-dd');
    try {
      await fetchWeekEntries(monthStart, monthEnd);
    } catch {
      addToast('Fehler beim Laden der Monatsdaten', 'error');
    }
  }, [viewMode, monthViewDate, fetchWeekEntries, addToast]);

  useEffect(() => {
    if (viewMode === 'month') {
      loadMonthData();
    }
  }, [viewMode, loadMonthData]);

  // When switching back to week view, reload week data
  // Skip the initial mount — loadAllData already runs via the [loadAllData] effect above
  const viewModeInitialized = useRef(false);
  useEffect(() => {
    if (!viewModeInitialized.current) {
      viewModeInitialized.current = true;
      return;
    }
    if (viewMode === 'week') {
      loadAllData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Initialize selected employees — exactly once, so that deselecting everything
  // survives the next refetch.
  const employeeSelectionInitialized = useRef(false);
  useEffect(() => {
    if (employeeSelectionInitialized.current) return;
    if (!weekData || weekData.employees.length === 0) return;

    if (!isAdmin) {
      // Mitarbeiter: nur eigene Daten — auf die asynchrone Auth-Info warten
      if (!authEmployee?.id) return;
      employeeSelectionInitialized.current = true;
      setSelectedEmployeeIds([authEmployee.id]);
      return;
    }

    // Die Rollen erreichen weekData erst einen Render nach der Mitarbeiterliste —
    // vorher wäre jeder Eintrag rollenlos und die Verwaltung würde mit ausgewählt.
    // Scheitert das Laden, ohne Rollen initialisieren statt gar nichts anzuzeigen.
    const rolesResolved = weekData.employees.some((e) => e.role !== undefined);
    const rosterFailed = !employeesLoading && employeesError !== null;
    if (!rolesResolved && !rosterFailed) return;

    // Admin: alle operativen Mitarbeiter, ohne Verwaltung/SuperAdmin
    employeeSelectionInitialized.current = true;
    const operational = weekData.employees.filter((e) => !isBackOfficeRole(e.role));
    const initial = operational.length > 0 ? operational : weekData.employees;
    setSelectedEmployeeIds(initial.map((e) => e.id));
  }, [weekData, isAdmin, authEmployee?.id, employeesLoading, employeesError]);

  // Month view: only one employee at a time
  useEffect(() => {
    if (viewMode !== 'month') return;
    setSelectedEmployeeIds((prev) => (prev.length > 1 ? [prev[0]] : prev));
  }, [viewMode]);

  // --- Navigation ---
  const handlePreviousWeek = () => {
    setCurrentDate((prev) => subWeeks(prev, 1));
  };

  const handleNextWeek = () => {
    setCurrentDate((prev) => addWeeks(prev, 1));
  };

  const handleToday = () => {
    if (viewMode === 'week') {
      setCurrentDate(startOfISOWeek(new Date()));
    } else if (viewMode === 'day') {
      setDayViewDate(new Date());
    } else {
      setMonthViewDate(new Date());
    }
  };

  const handlePreviousDay = () => setDayViewDate((prev) => subDays(prev, 1));
  const handleNextDay = () => setDayViewDate((prev) => addDays(prev, 1));

  const handlePreviousMonth = () => setMonthViewDate((prev) => subMonths(prev, 1));
  const handleNextMonth = () => setMonthViewDate((prev) => addMonths(prev, 1));

  const handleMonthDayClick = (date: Date) => {
    setDayViewDate(date);
    setViewMode('day');
  };

  const handleToggleEmployee = (employeeId: string) => {
    if (viewMode === 'month') {
      setSelectedEmployeeIds([employeeId]);
      return;
    }
    setSelectedEmployeeIds((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleToggleGroup = (employeeIds: string[], select: boolean) => {
    if (viewMode === 'month') {
      if (select && employeeIds.length > 0) setSelectedEmployeeIds([employeeIds[0]]);
      return;
    }
    if (select) {
      setSelectedEmployeeIds((prev) => [...new Set([...prev, ...employeeIds])]);
    } else {
      setSelectedEmployeeIds((prev) => prev.filter((id) => !employeeIds.includes(id)));
    }
  };

  // --- Drag & Drop ---
  const handleTaskDragStart = (taskId: string, employeeId: string, date: string) => {
    setDraggedTask({ taskId, sourceEmployeeId: employeeId, sourceDate: date });
  };

  const handleTaskDragEnd = () => {
    setDraggedTask(null);
  };

  const handleDayDrop = async (targetEmployeeId: string, targetDate: string) => {
    if (!draggedTask || !weekData) return;

    const sourceEmployee = weekData.employees.find((e) => e.id === draggedTask.sourceEmployeeId);
    const targetEmployee = weekData.employees.find((e) => e.id === targetEmployeeId);

    if (!sourceEmployee || !targetEmployee) return;

    const sourceDay = sourceEmployee.days.find((d) => d.date === draggedTask.sourceDate);
    const targetDay = targetEmployee.days.find((d) => d.date === targetDate);

    if (!sourceDay || !targetDay) return;

    const task = sourceDay.tasks.find((t) => t.id === draggedTask.taskId);
    if (!task) return;

    if (targetDay.type === 'absence') {
      addToast('Aufgabe kann nicht auf Abwesenheit verschoben werden', 'error');
      setDraggedTask(null);
      return;
    }

    if (targetDay.type === 'empty') {
      addToast('Bitte zuerst einen Arbeitstag anlegen', 'error');
      setDraggedTask(null);
      return;
    }

    if (!sourceDay.planningEntryId || !targetDay.planningEntryId) {
      addToast('Fehler: Planning Entry ID fehlt', 'error');
      setDraggedTask(null);
      return;
    }

    setMoveTaskDialog({
      isOpen: true,
      task,
      sourceDay,
      targetDay,
      targetEmployeeId,
      targetEmployeeName: targetEmployee.name,
      targetDate,
    });

    setDraggedTask(null);
  };

  // Due Task from sidepanel dropped onto a day cell or timeline row.
  // startTimeOverride lets the day-view timeline pre-fill the start from the drop x-position.
  const handleDueTaskDrop = (
    taskId: string,
    employeeId: string,
    date: string,
    durationMinutes: number,
    startTimeOverride?: string,
  ) => {
    if (!weekData) return;

    const employee = weekData.employees.find((e) => e.id === employeeId);
    if (!employee) return;

    const day = employee.days.find((d) => d.date === date);

    if (day?.type === 'absence') {
      addToast(t('dueTasks.cannotDropOnAbsence'), 'error');
      return;
    }

    // Pick start time: caller override, otherwise after the last existing task, otherwise 08:00
    let startTime = '08:00';
    if (startTimeOverride) {
      startTime = startTimeOverride;
    } else if (day && day.tasks.length > 0) {
      const lastTask = [...day.tasks].sort((a, b) => a.endTime.localeCompare(b.endTime)).pop();
      if (lastTask) {
        startTime = lastTask.endTime;
      }
    }

    // Calculate end time from estimated duration
    const [startH, startM] = startTime.split(':').map(Number);
    const totalMinutes = startH * 60 + startM + (durationMinutes || 60);
    const endH = Math.min(Math.floor(totalMinutes / 60), 23);
    const endM = totalMinutes % 60;
    const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;

    // Open AddTaskDialog with task pre-selected and times pre-filled
    setAddTaskDialog({
      isOpen: true,
      employeeId,
      employeeName: employee.name,
      date,
      planningEntryId: day?.planningEntryId,
      preSelectedTaskId: taskId,
      preStartTime: startTime,
      preEndTime: endTime,
    });
  };

  const handleConfirmMove = async (newStartTime: string, newEndTime: string) => {
    const { task, sourceDay, targetDay } = moveTaskDialog;
    if (!task || !sourceDay || !targetDay || !sourceDay.planningEntryId || !targetDay.planningEntryId) return;

    try {
      await moveTaskToEntry(
        task.id,
        sourceDay.planningEntryId,
        targetDay.planningEntryId,
        newStartTime,
        newEndTime
      );

      addToast('Aufgabe erfolgreich verschoben', 'success');

      setMoveTaskDialog({
        isOpen: false,
        task: null,
        sourceDay: null,
        targetDay: null,
        targetEmployeeId: '',
        targetEmployeeName: '',
        targetDate: '',
      });
    } catch (error) {
      addToast('Fehler beim Verschieben der Aufgabe', 'error');
      console.error('Move error:', error);
    }
  };

  const handleCancelMove = () => {
    setMoveTaskDialog({
      isOpen: false,
      task: null,
      sourceDay: null,
      targetDay: null,
      targetEmployeeId: '',
      targetEmployeeName: '',
      targetDate: '',
    });
  };

  // --- Dialog handlers ---
  const handleTaskClick = (taskId: string, employeeId: string, date: string) => {
    const employee = weekData?.employees.find((e) => e.id === employeeId);
    const day = employee?.days.find((d) => d.date === date);
    const task = day?.tasks.find((t) => t.id === taskId);

    if (task && employee && day) {
      setTaskEditDialog({
        isOpen: true,
        task,
        employeeId,
        employeeName: employee.name,
        date,
        currentDay: day,
      });
    }
  };

  const handleTaskEdit = async (taskId: string, updates: { startTime: string; endTime: string; notes?: string; applyToFuture: boolean }) => {
    if (!weekData) return;

    try {
      if (updates.applyToFuture) {
        const task = taskEditDialog.task;
        if (!task) return;

        const durationMinutes = calculateDurationMinutes(updates.startTime, updates.endTime);

        const result = await bulkUpdateTasks({
          taskId: taskId,
          employeeId: taskEditDialog.employeeId,
          fromDate: taskEditDialog.date,
          updates: {
            startTime: updates.startTime,
            endTime: updates.endTime,
            durationMinutes,
            notes: updates.notes || null,
          },
        });

        addToast(`${result.updatedCount || 0} Aufgaben aktualisiert`, 'success');
      } else {
        const currentDay = taskEditDialog.currentDay;
        if (!currentDay || !currentDay.planningEntryId) {
          addToast('Fehler: Planning Entry ID fehlt', 'error');
          return;
        }

        await updateTask(
          currentDay.planningEntryId,
          taskId,
          updates.startTime,
          updates.endTime,
          updates.notes
        );

        addToast('Aufgabe aktualisiert', 'success');
      }

      setTaskEditDialog({
        isOpen: false,
        task: null,
        employeeId: '',
        employeeName: '',
        date: '',
        currentDay: null,
      });
    } catch {
      addToast('Fehler beim Aktualisieren der Aufgabe', 'error');
    }
  };

  const handleDeleteTask = async (planningEntryTaskId: string, planningEntryId: string) => {
    try {
      await removeTaskFromEntry(planningEntryId, planningEntryTaskId);
      addToast('Aufgabe erfolgreich entfernt', 'success');
      setTaskEditDialog({
        isOpen: false,
        task: null,
        employeeId: '',
        employeeName: '',
        date: '',
        currentDay: null,
      });
      // Due Tasks neu laden - entfernte Aufgabe könnte wieder fällig sein
      fetchDueTasks(dueTasksContext.year, dueTasksContext.week).catch(() => {});
    } catch {
      addToast('Fehler beim Entfernen der Aufgabe', 'error');
    }
  };

  const calculateDurationMinutes = (startTime: string, endTime: string): number => {
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    return (endH * 60 + endM) - (startH * 60 + startM);
  };

  const handleAddAbsence = (employeeId: string, date: string) => {
    const employee = weekData?.employees.find((e) => e.id === employeeId);
    if (employee) {
      setAbsenceDialog({ isOpen: true, employeeId, employeeName: employee.name, date });
    }
  };

  const handleSaveAbsence = async (absenceTypeId: string, notes?: string) => {
    try {
      const employee = weekData?.employees.find((e) => e.id === absenceDialog.employeeId);
      const existingDay = employee?.days.find((d) => d.date === absenceDialog.date);

      if (existingDay && existingDay.planningEntryId && existingDay.type !== 'empty') {
        if (existingDay.tasks && existingDay.tasks.length > 0) {
          addToast('Tag kann nicht geändert werden - es sind bereits Aufgaben eingeplant', 'error');
          return;
        }
        await deleteEntry(existingDay.planningEntryId);
      }

      await createEntry({
        employeeId: absenceDialog.employeeId,
        date: absenceDialog.date,
        entryType: 'Absence',
        absenceTypeId: absenceTypeId,
        notes: notes || null,
      });

      addToast('Abwesenheit erfolgreich eingetragen', 'success');
      setAbsenceDialog({ isOpen: false, employeeId: '', employeeName: '', date: '' });
    } catch (err) {
      showApiError(err);
    }
  };

  const openAssignModelDialog = (employeeId: string, employeeName?: string) => {
    const name =
      employeeName ??
      weekData?.employees.find((e) => e.id === employeeId)?.name ??
      allEmployees.find((e) => e.id === employeeId)?.fullName ??
      '';
    setAssignModelDialog({ isOpen: true, employeeId, employeeName: name });
  };

  // Toast-CTA for the BE error "employee has no work schedule model": opens the assign dialog.
  const workScheduleModelCodeAction = (employeeId: string) => ({
    'planning.planningEntries.employeeWithoutWorkScheduleModel': {
      label: tWorkScheduleModels('assign.title'),
      onClick: () => openAssignModelDialog(employeeId),
    },
  });

  const handleAddWorkDay = (employeeId: string, date: string) => {
    const employee = weekData?.employees.find((e) => e.id === employeeId);
    if (!employee) return;

    // Blocked with a path forward: the BE rejects WorkDay entries for employees without a
    // work schedule model — offer the assignment right away instead of running into the 400.
    if (isAdmin && employee.workScheduleModelId === null) {
      openAssignModelDialog(employeeId, employee.name);
      return;
    }

    setWorkDayDialog({ isOpen: true, employeeId, employeeName: employee.name, date });
  };

  /** Saves the planned vehicle only when it changed — tenants without vehicles never call it. */
  const savePlannedVehicle = async (planningEntryId: string, vehicle: PlannedVehicleChoice) => {
    const before = workDayDialog.initialVehicle;
    const unchanged =
      (before?.vehicleId ?? null) === vehicle.vehicleId &&
      (before?.driverRole ?? null) === vehicle.driverRole;
    if (activeVehicles.length === 0 || unchanged) return;
    await setPlannedVehicle(planningEntryId, vehicle.vehicleId, vehicle.driverRole);
  };

  const handleSaveWorkDay = async (
    startTime: string,
    endTime: string,
    targetHours: number,
    vehicle: PlannedVehicleChoice,
  ) => {
    try {
      // Edit mode: update existing entry
      if (workDayDialog.isEditMode && workDayDialog.planningEntryId) {
        await savePlannedVehicle(workDayDialog.planningEntryId, vehicle);
        await updateEntry(workDayDialog.planningEntryId, {
          // why: entryType is required by UpdatePlanningEntryRequest. This dialog only ever
          // edits work days, matching the 'WorkDay' value used on the create paths below.
          entryType: 'WorkDay',
          startTime,
          endTime,
          targetHours,
        });
        addToast('Tag erfolgreich aktualisiert', 'success');
        setWorkDayDialog({ isOpen: false, employeeId: '', employeeName: '', date: '' });
        return;
      }

      // Create mode: create new entry
      const employee = weekData?.employees.find((e) => e.id === workDayDialog.employeeId);
      const existingDay = employee?.days.find((d) => d.date === workDayDialog.date);

      if (existingDay && existingDay.planningEntryId && existingDay.type !== 'empty') {
        if (existingDay.tasks && existingDay.tasks.length > 0) {
          addToast('Tag kann nicht geändert werden - es sind bereits Aufgaben eingeplant', 'error');
          return;
        }
        await deleteEntry(existingDay.planningEntryId);
      }

      const created = await createEntry({
        employeeId: workDayDialog.employeeId,
        date: workDayDialog.date,
        entryType: 'WorkDay',
        startTime: startTime,
        endTime: endTime,
        targetHours: targetHours,
        breakMinutes: 0,
        notes: null,
      });
      if (created?.planningEntryId) {
        await savePlannedVehicle(created.planningEntryId, vehicle);
      }

      addToast('Arbeitstag erfolgreich angelegt', 'success');
      setWorkDayDialog({ isOpen: false, employeeId: '', employeeName: '', date: '' });
    } catch (err) {
      showApiError(err, { codeActions: workScheduleModelCodeAction(workDayDialog.employeeId) });
    }
  };

  const handleAddTask = (employeeId: string, date: string) => {
    const employee = weekData?.employees.find((e) => e.id === employeeId);
    if (!employee) return;

    const day = employee.days.find((d) => d.date === date);

    setAddTaskDialog({
      isOpen: true,
      employeeId,
      employeeName: employee.name,
      date,
      planningEntryId: day?.planningEntryId,
    });
  };

  // Other employees with a WorkDay on the dialog's date — candidates for planning the same
  // task for several people at once (FACF-111).
  const addTaskCoworkers = useMemo<AddTaskCoworker[]>(() => {
    if (!addTaskDialog.isOpen) return [];
    const day = addTaskDialog.date.slice(0, 10);
    const coworkers = new Map<string, string>();
    for (const entry of entries) {
      if (
        entry.employeeId &&
        entry.employeeId !== addTaskDialog.employeeId &&
        entry.entryType === 'WorkDay' &&
        entry.date?.slice(0, 10) === day
      ) {
        coworkers.set(entry.employeeId, entry.employeeName ?? '');
      }
    }
    return [...coworkers]
      .map(([employeeId, name]) => ({ employeeId, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [entries, addTaskDialog.isOpen, addTaskDialog.date, addTaskDialog.employeeId]);

  const handleSaveTask = async (
    taskId: string,
    startTime: string,
    endTime: string,
    coworkerIds: string[]
  ) => {
    try {
      const { planningEntryId } = addTaskDialog;

      let entryId = planningEntryId;
      if (!entryId) {
        const durationMinutes = calculateDurationMinutes(startTime, endTime);
        const targetHours = durationMinutes / 60;

        const newEntry = await createEntry({
          employeeId: addTaskDialog.employeeId,
          date: addTaskDialog.date,
          entryType: 'WorkDay',
          startTime: startTime,
          endTime: endTime,
          targetHours: targetHours,
          breakMinutes: 0,
          notes: null,
        });

        entryId = newEntry.planningEntryId;
      }

      if (coworkerIds.length > 0) {
        await assignTaskToEmployees({
          taskId,
          date: addTaskDialog.date,
          employeeIds: [addTaskDialog.employeeId, ...coworkerIds],
          startTime,
          endTime,
          durationMinutes: calculateDurationMinutes(startTime, endTime),
        });
      } else if (entryId) {
        await addTaskToEntry(entryId, taskId, startTime, endTime);
      }

      addToast(t('messages.taskAssigned'), 'success');

      // Optimistic: den verplanten Fälligkeitstag sofort aus der Karte nehmen
      markDueTaskPlanned(taskId, addTaskDialog.date);

      setAddTaskDialog({ isOpen: false, employeeId: '', employeeName: '', date: '' });

      // Background: Due Tasks vom BE synchronisieren
      fetchDueTasks(dueTasksContext.year, dueTasksContext.week).catch(() => {});
    } catch (err) {
      showApiError(err, { codeActions: workScheduleModelCodeAction(addTaskDialog.employeeId) });
    }
  };

  const handleEditDay = (employeeId: string, date: string) => {
    const employee = weekData?.employees.find((e) => e.id === employeeId);
    if (!employee) return;

    const day = employee.days.find((d) => d.date === date);
    if (!day || !day.planningEntryId) return;

    // Find the original entry to get start/end times
    const entry = entries.find((e) => e.planningEntryId === day.planningEntryId);

    setWorkDayDialog({
      isOpen: true,
      employeeId,
      employeeName: employee.name,
      date,
      isEditMode: true,
      planningEntryId: day.planningEntryId,
      initialStartTime: entry?.startTime?.slice(0, 5) || '07:00',
      initialEndTime: entry?.endTime?.slice(0, 5) || '15:00',
      initialTargetHours: entry?.targetHours ?? day.targetHours ?? 8,
      initialVehicle: {
        vehicleId: entry?.plannedVehicleId ?? null,
        driverRole: entry?.plannedDriverRole ?? null,
      },
    });
  };

  const handleRemoveDay = (employeeId: string, date: string) => {
    if (!weekData) return;

    const employee = weekData.employees.find((e) => e.id === employeeId);
    const day = employee?.days.find((d) => d.date === date);

    if (!day?.planningEntryId) return;

    setRemoveDayDialog({ isOpen: true, employeeId, date });
  };

  const closeRemoveDayDialog = () => setRemoveDayDialog({ isOpen: false, employeeId: '', date: '' });

  const handleConfirmRemoveDay = async () => {
    if (!weekData) return;

    const { employeeId, date } = removeDayDialog;
    const employee = weekData.employees.find((e) => e.id === employeeId);
    const day = employee?.days.find((d) => d.date === date);

    if (!day?.planningEntryId) {
      closeRemoveDayDialog();
      return;
    }

    try {
      await deleteEntry(day.planningEntryId);
      addToast('Tag erfolgreich gelöscht', 'success');
      closeRemoveDayDialog();

      const newWeekData = JSON.parse(JSON.stringify(weekData)) as PlanningWeekData;
      const empIndex = newWeekData.employees.findIndex((e) => e.id === employeeId);
      if (empIndex !== -1) {
        const dayIndex = newWeekData.employees[empIndex].days.findIndex((d) => d.date === date);
        if (dayIndex !== -1) {
          newWeekData.employees[empIndex].days[dayIndex] = {
            date,
            type: 'empty',
            tasks: [],
            plannedHours: 0,
            targetHours: 0,
          };
        }
      }
      setWeekData(newWeekData);
    } catch {
      addToast('Fehler beim Löschen des Tags', 'error');
    }
  };

  const handleGenerateMonth = async (month: number, year: number) => {
    try {
      await generateMonthEntries(selectedEmployeeIds, month, year);
      addToast('Arbeitsmonat erfolgreich generiert', 'success');
      await loadAllData();
    } catch {
      addToast('Fehler beim Generieren des Arbeitsmonats', 'error');
    }
  };

  const handleWeekCopied = (copiedCount: number, targetWeek: number, targetYear: number) => {
    addToast(t('copyWeek.toast', { count: copiedCount, week: targetWeek, year: targetYear }), copiedCount > 0 ? 'success' : 'info');
  };

  const handleCopyWeekComplete = () => {
    loadAllData();
  };

  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!exportMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportMenuOpen]);

  const handleExportPdf = (type: ReportType, includeChecklists = false) => {
    setExportMenuOpen(false);
    const employeeId = isAdmin && selectedEmployeeIds.length === 1 ? selectedEmployeeIds[0] : undefined;

    if (type === 'weekly') {
      downloadReportFn('weekly', { year: currentYear, week: currentWeek }, employeeId);
    } else if (type === 'daily') {
      const dateToUse = viewMode === 'day' ? dayViewDate : new Date();
      downloadReportFn(
        'daily',
        { date: format(dateToUse, 'yyyy-MM-dd'), includeChecklists },
        employeeId,
      );
    } else {
      // Derive month from the current context: month view → monthViewDate, week view → currentDate, else → now
      const refDate = viewMode === 'month' ? monthViewDate : viewMode === 'week' ? currentDate : new Date();
      const year = refDate.getFullYear();
      const month = refDate.getMonth() + 1;
      downloadReportFn('monthly', { year, month }, employeeId);
    }
  };

  const handleDueTaskClick = () => {
    addToast(t(viewMode === 'day' ? 'dueTasks.dragHintDay' : 'dueTasks.dragHint'), 'info');
  };

  // --- Day View Handlers ---
  const handleDayViewAddTask = (entry: typeof entries[number], startTime?: string) => {
    // Outlook-style: clicking on the timeline at e.g. 10:30 opens AddTaskDialog
    // pre-filled with startTime=10:30 and a default 1h block (endTime=11:30).
    let preStartTime: string | undefined;
    let preEndTime: string | undefined;
    if (startTime) {
      preStartTime = startTime;
      const [h, m] = startTime.split(':').map(Number);
      const endTotal = Math.min(23 * 60 + 59, h * 60 + m + 60);
      const eh = Math.floor(endTotal / 60);
      const em = endTotal % 60;
      preEndTime = `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
    }
    setAddTaskDialog({
      isOpen: true,
      employeeId: entry.employeeId || '',
      employeeName: entry.employeeName || '',
      date: entry.date || '',
      planningEntryId: entry.planningEntryId || undefined,
      preStartTime,
      preEndTime,
    });
  };

  // Due task dropped onto a timeline row. Unlike the week view this resolves the entry
  // straight from `entries` — weekData is bound to the week-view week, so a day outside
  // it would report "no entry" and create a duplicate work day.
  const handleDayViewDueTaskDrop = (
    taskId: string,
    employeeId: string,
    date: string,
    durationMinutes: number,
    startTimeOverride?: string,
  ) => {
    const entry = entries.find((e) => e.employeeId === employeeId && e.date === date);

    if ((entry?.entryType ?? '').toLowerCase() === 'absence') {
      addToast(t('dueTasks.cannotDropOnAbsence'), 'error');
      return;
    }

    // Pick start time: drop position, otherwise after the last existing task, otherwise 08:00
    let startTime = '08:00';
    if (startTimeOverride) {
      startTime = startTimeOverride;
    } else {
      const lastEnd = (entry?.tasks ?? [])
        .map((task) => task.endTime?.slice(0, 5))
        .filter((time): time is string => !!time)
        .sort()
        .pop();
      if (lastEnd) startTime = lastEnd;
    }

    const [startH, startM] = startTime.split(':').map(Number);
    const totalMinutes = startH * 60 + startM + (durationMinutes || 60);
    const endH = Math.min(Math.floor(totalMinutes / 60), 23);
    const endM = totalMinutes % 60;
    const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;

    const employeeName =
      entry?.employeeName ??
      weekData?.employees.find((e) => e.id === employeeId)?.name ??
      '';

    setAddTaskDialog({
      isOpen: true,
      employeeId,
      employeeName,
      date,
      planningEntryId: entry?.planningEntryId || undefined,
      preSelectedTaskId: taskId,
      preStartTime: startTime,
      preEndTime: endTime,
    });
  };

  const handleDayViewTaskClick = (task: NonNullable<typeof entries[number]['tasks']>[number], entry: typeof entries[number]) => {
    const taskBlock: TaskBlock = {
      id: task.planningEntryTaskId || '',
      taskId: task.taskId || '',
      taskName: task.taskName || '',
      objectName: task.objectName || '',
      serviceName: task.serviceName || '',
      startTime: task.startTime?.slice(0, 5) || '08:00',
      endTime: task.endTime?.slice(0, 5) || '09:00',
      durationHours: (task.durationMinutes || 0) / 60,
    };

    const dayPlanning: DayPlanning = {
      date: entry.date || '',
      type: 'workday',
      planningEntryId: entry.planningEntryId || undefined,
      targetHours: entry.targetHours || 0,
      plannedHours: entry.plannedHours || 0,
      tasks: (entry.tasks || []).map((t) => ({
        id: t.planningEntryTaskId || '',
        taskId: t.taskId || '',
        taskName: t.taskName || '',
        objectName: t.objectName || '',
        serviceName: t.serviceName || '',
        startTime: t.startTime?.slice(0, 5) || '',
        endTime: t.endTime?.slice(0, 5) || '',
        durationHours: (t.durationMinutes || 0) / 60,
      })),
    };

    setTaskEditDialog({
      isOpen: true,
      task: taskBlock,
      employeeId: entry.employeeId || '',
      employeeName: entry.employeeName || '',
      date: entry.date || '',
      currentDay: dayPlanning,
    });
  };

  // --- Conflict Detection (Admin only) ---
  const conflicts = useMemo(() => {
    if (!isAdmin || !weekData) return [];
    const result: { employeeId: string; employeeName: string; date: string; absenceType: string; taskCount: number; taskNames: string[] }[] = [];
    for (const emp of weekData.employees) {
      for (const day of emp.days) {
        if (day.absenceConflict && day.tasks.length > 0) {
          result.push({
            employeeId: emp.id,
            employeeName: emp.name,
            date: day.date,
            absenceType: day.absenceConflict.absenceTypeName,
            taskCount: day.tasks.length,
            taskNames: day.tasks.map((t) => t.objectName || t.taskName).filter(Boolean),
          });
        }
      }
    }
    return result;
  }, [isAdmin, weekData]);

  // --- Render ---
  const isWeekLoading = viewMode === 'week' && (loading || !weekData);
  const isDayMonthLoading = (viewMode === 'day' || viewMode === 'month') && loading;

  if (isWeekLoading) {
    return <Loading />;
  }

  const filteredEmployees = weekData?.employees.filter((e) =>
    selectedEmployeeIds.includes(e.id)
  ) || [];

  const viewTabs: { key: ViewMode; label: string; icon: React.ReactNode }[] = [
    { key: 'week', label: 'Woche', icon: <CalendarRange className="w-3.5 h-3.5" strokeWidth={2} /> },
    { key: 'day', label: 'Tag', icon: <CalendarDays className="w-3.5 h-3.5" strokeWidth={2} /> },
    { key: 'month', label: 'Monat', icon: <Calendar className="w-3.5 h-3.5" strokeWidth={2} /> },
  ];

  const subtitle =
    viewMode === 'week'
      ? `KW ${currentWeek} · ${format(currentDate, 'd. MMM', { locale: de })} – ${format(addDays(currentDate, 6), 'd. MMM yyyy', { locale: de })}`
      : viewMode === 'day'
        ? format(dayViewDate, 'EEEE, d. MMMM yyyy', { locale: de })
        : format(monthViewDate, 'MMMM yyyy', { locale: de });

  const handlePrev =
    viewMode === 'week' ? handlePreviousWeek : viewMode === 'day' ? handlePreviousDay : handlePreviousMonth;
  const handleNext =
    viewMode === 'week' ? handleNextWeek : viewMode === 'day' ? handleNextDay : handleNextMonth;

  return (
    <div className="flex flex-col h-full min-h-0 gap-4">
      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 flex-shrink-0">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {/* Left: title + period */}
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              Dienstplanung
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5 tabular-nums">
              {subtitle}
            </p>
          </div>

          {/* Right: view tabs + nav + actions */}
          <div className="flex flex-wrap items-center gap-2">
            {/* View tabs */}
            <div className="inline-flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5">
              {viewTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setViewMode(tab.key)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[12px] font-medium transition-colors ${
                    viewMode === tab.key
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Nav */}
            <div className="inline-flex items-center gap-1">
              <button
                type="button"
                onClick={handleToday}
                className="inline-flex items-center px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[12px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Heute
              </button>
              <button
                type="button"
                onClick={handlePrev}
                aria-label="Zurück"
                className="p-1.5 rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={handleNext}
                aria-label="Vorwärts"
                className="p-1.5 rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <ChevronRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>

            {/* Spacer */}
            <span className="hidden lg:inline-block w-px h-6 bg-slate-200 mx-1" />

            {/* Admin actions for week view */}
            {isAdmin && viewMode === 'week' && (
              <>
                <button
                  type="button"
                  onClick={() => setAdHocSlideOverOpen(true)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[12px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <CalendarPlus className="w-3.5 h-3.5" strokeWidth={2} />
                  Sondertätigkeit
                </button>
                <button
                  type="button"
                  onClick={() => setCopyWeekDialog(true)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[12px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" strokeWidth={2} />
                  Woche übernehmen
                </button>
                <button
                  type="button"
                  onClick={() => setGenerateMonthDialog(true)}
                  disabled={selectedEmployeeIds.length === 0}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#00A871] hover:bg-emerald-700 text-white text-[12px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CalendarPlus className="w-3.5 h-3.5" strokeWidth={2} />
                  Monat generieren
                </button>
              </>
            )}

            {/* Planning assistant — opens the displayed day (day view) or today */}
            {isAdmin && (
              <Link
                to={`/planning/assistant?date=${isoDate(viewMode === 'day' ? dayViewDate : new Date())}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[12px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Zap className="w-3.5 h-3.5" strokeWidth={2} />
                {t('assistant.openAssistant')}
              </Link>
            )}

            {/* PDF export — always */}
            <div className="relative" ref={exportMenuRef}>
              <button
                type="button"
                onClick={() => setExportMenuOpen((o) => !o)}
                disabled={reportDownloading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[12px] font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileDown className="w-3.5 h-3.5" strokeWidth={2} />
                {tReports('actions.exportPdf')}
              </button>
              {exportMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-56 rounded-md border border-slate-200 bg-white py-1 z-50"
                  style={{ boxShadow: '0 10px 30px -10px rgba(15,23,42,0.15)' }}
                >
                  <button onClick={() => handleExportPdf('weekly')} className="w-full text-left px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50">
                    {tReports('types.weekly')}
                  </button>
                  <button onClick={() => handleExportPdf('daily')} className="w-full text-left px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50">
                    {tReports('types.daily')}
                  </button>
                  <button onClick={() => handleExportPdf('daily', true)} className="w-full text-left px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50">
                    {tReports('types.dailyWithChecklists')}
                  </button>
                  <button onClick={() => handleExportPdf('monthly')} className="w-full text-left px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50">
                    {tReports('types.monthly')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isDayMonthLoading && <Loading />}

      {/* Conflict Alert Banner (Admin only) */}
      {isAdmin && conflicts.length > 0 && viewMode === 'week' && (
        <div className="flex-shrink-0 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div className="flex-1 min-w-0 space-y-1">
              <h3 className="text-[13px] font-semibold text-red-800">
                {conflicts.length === 1 ? '1 Konflikt' : `${conflicts.length} Konflikte`} erkannt
              </h3>
              <ul className="space-y-0.5 text-[12px] text-red-700">
                {conflicts.map((c) => {
                  const dateObj = new Date(c.date + 'T00:00:00');
                  const dateStr = format(dateObj, 'EEE, d. MMM', { locale: de });
                  return (
                    <li
                      key={`${c.employeeId}-${c.date}`}
                      className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5"
                    >
                      <span className="font-medium text-red-800">{c.employeeName}</span>
                      <span className="text-red-400">·</span>
                      <span className="tabular-nums">{dateStr}</span>
                      <span className="text-red-400">·</span>
                      <span className="text-[11px] font-medium uppercase tracking-wider text-red-700">
                        {c.absenceType}
                      </span>
                      <span className="text-red-400">aber</span>
                      <span>
                        <span className="tabular-nums">{c.taskCount}</span> {c.taskCount === 1 ? 'Aufgabe' : 'Aufgaben'} eingeplant
                        {c.taskNames.length > 0 && (
                          <span className="text-red-500"> ({c.taskNames.join(', ')})</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* WEEK VIEW */}
      {viewMode === 'week' && weekData && (
        <div className="flex flex-col flex-1 min-h-0">
          {weekData.employees.length === 0 ? (
            <Card>
              <EmptyState
                icon={<Calendar className="w-12 h-12" />}
                title="Keine Mitarbeiter vorhanden"
                description="Erstellen Sie zunächst Mitarbeiter, um mit der Dienstplanung zu beginnen."
              />
            </Card>
          ) : (
            <>
              {/* Employee Selector (Admin only) */}
              {isAdmin && (
                <div className="flex-shrink-0 mb-3">
                  <EmployeeSelector
                    employees={weekData.employees}
                    selectedEmployeeIds={selectedEmployeeIds}
                    onToggleEmployee={handleToggleEmployee}
                    onToggleGroup={handleToggleGroup}
                  />
                </div>
              )}

              {/* Main Layout: 70% Tabelle, 20% Fällige Aufgaben – bei Full HD ohne Seiten-Scrollbar */}
              <div className="flex gap-4 flex-1 min-h-0">
                {/* Week Grid – 70% */}
                <div className="flex-[7] min-w-0 min-h-0 overflow-auto bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-secondary-300 dark:border-dark-border">
                  {/* Inner wrapper: min-width ensures horizontal scroll on small screens; grows with container on large ones */}
                  <div className="min-w-[1120px]">
                    <WeekHeader
                      weekStart={weekData.weekStart}
                      weekNumber={currentWeek}
                      onPreviousWeek={handlePreviousWeek}
                      onNextWeek={handleNextWeek}
                      onToday={handleToday}
                      employeeCount={filteredEmployees.length}
                      taskCount={filteredEmployees.reduce(
                        (sum, e) => sum + e.days.reduce((s, d) => s + d.tasks.length, 0),
                        0,
                      )}
                      conflictCount={conflicts.length}
                    />

                    <div>
                      {filteredEmployees.map((emp) => (
                        <EmployeeRow
                        key={emp.id}
                        employee={emp}
                        {...(isAdmin ? {
                          onTaskDragStart: handleTaskDragStart,
                          onTaskDragEnd: handleTaskDragEnd,
                          onDayDrop: handleDayDrop,
                          onDueTaskDrop: handleDueTaskDrop,
                          onTaskClick: handleTaskClick,
                          onAddTask: handleAddTask,
                          onAddAbsence: handleAddAbsence,
                          onAddWorkDay: handleAddWorkDay,
                          onEditDay: handleEditDay,
                          onRemoveDay: handleRemoveDay,
                          onAssignWorkScheduleModel: openAssignModelDialog,
                        } : {})}
                      />
                    ))}
                    </div>
                  </div>
                </div>

                {/* Due Tasks Sidepanel – 20% (nur für Admin) */}
                {isAdmin && (
                  <div className="flex-[2] min-w-0 min-h-0 hidden lg:flex flex-col overflow-hidden">
                    <DueTasksSidepanel
                      dueTasks={dueTasks}
                      loading={dueTasksLoading}
                      year={currentYear}
                      week={currentWeek}
                      onTaskClick={handleDueTaskClick}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* DAY VIEW */}
      {viewMode === 'day' && !isDayMonthLoading && (
        <div className="flex flex-col flex-1 min-h-0">
          {isAdmin && weekData && weekData.employees.length > 0 && (
            <div className="flex-shrink-0 mb-3">
              <EmployeeSelector
                employees={weekData.employees}
                selectedEmployeeIds={selectedEmployeeIds}
                onToggleEmployee={handleToggleEmployee}
                onToggleGroup={handleToggleGroup}
              />
            </div>
          )}

          {/* Main Layout: Timeline links, fällige Aufgaben des Tages rechts */}
          <div className="flex gap-4 flex-1 min-h-0">
            <div className="flex-[7] min-w-0 min-h-0 overflow-auto">
              <DayView
                date={dayViewDate}
                entries={entries}
                selectedEmployeeIds={selectedEmployeeIds}
                selectableEmployees={weekData?.employees.map((e) => ({ id: e.id, name: e.name, avatarColor: e.avatarColor, role: e.role })) ?? []}
                {...(isAdmin ? {
                  onAddTask: handleDayViewAddTask,
                  onTaskClick: handleDayViewTaskClick,
                  onAddWorkDay: handleAddWorkDay,
                  onDueTaskDrop: handleDayViewDueTaskDrop,
                } : {})}
              />
            </div>

            {isAdmin && (
              <div className="flex-[2] min-w-0 min-h-0 hidden lg:flex flex-col overflow-hidden">
                <DueTasksSidepanel
                  dueTasks={dueTasks}
                  loading={dueTasksLoading}
                  year={dueTasksContext.year}
                  week={dueTasksContext.week}
                  date={format(dayViewDate, 'yyyy-MM-dd')}
                  onTaskClick={handleDueTaskClick}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* MONTH VIEW */}
      {viewMode === 'month' && !isDayMonthLoading && (
        <>
          {isAdmin && weekData && weekData.employees.length > 0 && (
            <EmployeeSelector
              employees={weekData.employees}
              selectedEmployeeIds={selectedEmployeeIds}
              onToggleEmployee={handleToggleEmployee}
              onToggleGroup={handleToggleGroup}
            />
          )}
          <MonthView
            month={monthViewDate}
            entries={entries}
            selectedEmployeeIds={selectedEmployeeIds}
            {...(isAdmin ? { onDayClick: handleMonthDayClick } : {})}
          />
        </>
      )}

      {/* Dialogs */}
      <TaskEditDialog
        isOpen={taskEditDialog.isOpen}
        onClose={() => setTaskEditDialog({
          isOpen: false,
          task: null,
          employeeId: '',
          employeeName: '',
          date: '',
          currentDay: null,
        })}
        task={taskEditDialog.task}
        currentDay={taskEditDialog.currentDay}
        employeeName={taskEditDialog.employeeName}
        date={taskEditDialog.date}
        onSave={handleTaskEdit}
        onDelete={handleDeleteTask}
      />

      <AbsenceDialog
        isOpen={absenceDialog.isOpen}
        onClose={() => setAbsenceDialog({ isOpen: false, employeeId: '', employeeName: '', date: '' })}
        date={absenceDialog.date}
        employeeName={absenceDialog.employeeName}
        onSave={handleSaveAbsence}
      />

      <WorkDayDialog
        isOpen={workDayDialog.isOpen}
        onClose={() => setWorkDayDialog({ isOpen: false, employeeId: '', employeeName: '', date: '' })}
        date={workDayDialog.date}
        employeeName={workDayDialog.employeeName}
        onSave={handleSaveWorkDay}
        isEditMode={workDayDialog.isEditMode}
        initialStartTime={workDayDialog.initialStartTime}
        initialEndTime={workDayDialog.initialEndTime}
        initialTargetHours={workDayDialog.initialTargetHours}
        vehicles={activeVehicles}
        initialVehicle={workDayDialog.initialVehicle}
      />

      <AddTaskDialog
        isOpen={addTaskDialog.isOpen}
        onClose={() => setAddTaskDialog({ isOpen: false, employeeId: '', employeeName: '', date: '' })}
        date={addTaskDialog.date}
        employeeName={addTaskDialog.employeeName}
        planningEntryId={addTaskDialog.planningEntryId}
        preSelectedTaskId={addTaskDialog.preSelectedTaskId}
        preStartTime={addTaskDialog.preStartTime}
        preEndTime={addTaskDialog.preEndTime}
        coworkers={addTaskCoworkers}
        onSave={handleSaveTask}
      />

      <MoveTaskDialog
        isOpen={moveTaskDialog.isOpen}
        task={moveTaskDialog.task}
        targetDay={moveTaskDialog.targetDay}
        targetEmployeeName={moveTaskDialog.targetEmployeeName}
        onConfirm={handleConfirmMove}
        onCancel={handleCancelMove}
      />

      <GenerateMonthDialog
        isOpen={generateMonthDialog}
        onClose={() => setGenerateMonthDialog(false)}
        selectedEmployeeCount={selectedEmployeeIds.length}
        onGenerate={handleGenerateMonth}
      />

      <CopyWeekDialog
        isOpen={copyWeekDialog}
        onClose={() => setCopyWeekDialog(false)}
        sourceYear={currentYear}
        sourceWeek={currentWeek}
        onCopied={handleWeekCopied}
        onCopyComplete={handleCopyWeekComplete}
        employees={weekData?.employees || []}
        selectedEmployeeIds={selectedEmployeeIds}
      />

      <AdHocPlanEntrySlideOver
        isOpen={adHocSlideOverOpen}
        onClose={() => setAdHocSlideOverOpen(false)}
        onCreated={() => {
          const weekEnd = getWeekEnd(currentWeekStart);
          fetchWeekEntries(currentWeekStart, weekEnd);
        }}
      />

      <AssignWorkScheduleModelDialog
        isOpen={assignModelDialog.isOpen}
        employeeId={assignModelDialog.employeeId}
        employeeName={assignModelDialog.employeeName}
        currentModelId={null}
        onClose={() => setAssignModelDialog({ isOpen: false, employeeId: '', employeeName: '' })}
        onAssigned={() => {
          // The BE regenerated the next 90 days — refetch entries + employee list (badge state).
          loadAllData();
        }}
      />

      <ConfirmDialog
        isOpen={removeDayDialog.isOpen}
        onClose={closeRemoveDayDialog}
        onConfirm={handleConfirmRemoveDay}
        title={t('messages.removeDayTitle')}
        message={t('messages.removeDayConfirm')}
        confirmText={t('common:actions.delete')}
        variant="danger"
      />
    </div>
  );
}
